import * as Sentry from '@sentry/react'
import { getActiveThemeSlug } from '@/themes/registry'
import { getThemedOverrideLoader, type ModuleLoader } from './overrideMap'
import { pushThemeDevFallback } from './themeDevOverlayStore'
import { withThemeErrorBoundary } from './withThemeErrorBoundary'
import { isThemeComponent } from './isThemeComponent'
import type React from 'react'

/** Typed resolution failure for a themed screen/component path. */
export class ThemeResolutionError extends Error {
  public readonly sourcePath: string
  public readonly themeSlug: string

  public constructor(sourcePath: string, themeSlug: string, cause?: unknown) {
    super(`Unable to resolve themed component [${sourcePath}] for theme [${themeSlug}].`, { cause })
    this.name = 'ThemeResolutionError'
    this.sourcePath = sourcePath
    this.themeSlug = themeSlug
  }
}

/**
 * Resolves a page or component for the active build-time theme.
 * Loads at most one dynamic import: theme override when present, otherwise the source module.
 * On override load failure, falls back to the source module and reports to Sentry / dev overlay.
 */
export async function resolveThemedComponent<Props extends object = Record<string, never>>(
  sourceImport: ModuleLoader,
  sourcePath: string,
  exportName: string,
): Promise<{ default: React.ComponentType<Props> }> {
  const themeSlug = getActiveThemeSlug()
  const overrideImport = await getThemedOverrideLoader(sourcePath, themeSlug)
  const selectedImport = overrideImport ?? sourceImport

  try {
    const selectedModule = await selectedImport()
    const selectedComponent = moduleExport(selectedModule, exportName)
    if (!isThemeComponent(selectedComponent)) {
      throw new ThemeResolutionError(sourcePath, themeSlug)
    }

    if (overrideImport === undefined) {
      return { default: selectedComponent as React.ComponentType<Props> }
    }

    return {
      default: withThemeErrorBoundary(
        selectedComponent as React.ComponentType,
        async () => {
          const defaultModule = await sourceImport()
          const defaultComponent = moduleExport(defaultModule, exportName)
          if (!isThemeComponent(defaultComponent)) {
            throw new ThemeResolutionError(sourcePath, 'default')
          }

          return defaultComponent as React.ComponentType
        },
        sourcePath,
        themeSlug,
      ) as React.ComponentType<Props>,
    }
  } catch (error) {
    if (overrideImport === undefined) {
      throw error
    }

    const resolutionError = new ThemeResolutionError(sourcePath, themeSlug, error)
    Sentry.captureException(resolutionError, {
      tags: {
        event: 'theme_resolution_fallback',
        theme_slug: themeSlug,
      },
      contexts: {
        theme: {
          source_path: sourcePath,
        },
      },
    })
    pushThemeDevFallback({
      sourcePath,
      themeSlug,
      message: resolutionError.message,
      stack: error instanceof Error ? error.stack : undefined,
    })

    const defaultModule = await sourceImport()
    const defaultComponent = moduleExport(defaultModule, exportName)
    if (!isThemeComponent(defaultComponent)) {
      throw new ThemeResolutionError(sourcePath, 'default')
    }

    return { default: defaultComponent as React.ComponentType<Props> }
  }
}

/** Hook alias returning {@link resolveThemedComponent} for template-tag style usage. */
export function useThemedComponent(): typeof resolveThemedComponent {
  return resolveThemedComponent
}

function moduleExport(module: unknown, exportName: string): unknown {
  if (typeof module !== 'object' || module === null) {
    return undefined
  }

  return (module as Record<string, unknown>)[exportName]
}
